/**
 * ReportsPage — Task 289A: the monthly client operational report.
 *
 * Task 309A mounts a SECOND, independent section below it
 * (`OrganicPerformanceSection`) with its own endpoint, its own period and
 * platform filters and its own fetch. The two answer different questions —
 * "did the work ship?" and "how did it perform?" — and neither is derivable
 * from the other, so this page now issues two requests rather than one.
 *
 * Replaces the Task 041 report-*jobs* browser (an inert scaffold with
 * disabled coming-soon actions and no shipped download EF) with the
 * first genuinely usable report: for one
 * client and one month — what was planned, what is in production,
 * what awaits the client, what is approved/ready, and what actually
 * published. The async export pipeline (`report_jobs`) remains intact
 * at the DB layer and on the agency page; it is simply no longer this
 * route's face (tasks/289A §1.1). The agency-side `/app/reports` page
 * is untouched.
 *
 * Data: ONE call to `get-client-monthly-report` (Task 289A EF) —
 * server-side merge of calendar_entries (the plan) and
 * publish_jobs.completed_at (the publish fact), computed under the
 * CALLER's RLS. No service_role anywhere, no invented metrics, no
 * on-time rate (not derivable — tasks/289A §2).
 *
 * Client-viewer privacy (286G posture):
 *   - `viewerIsClient` re-derived locally exactly like ContentPage
 *     (client-category role AND not agency).
 *   - Agency-only buckets (planned / in_production / cancelled) are
 *     DOUBLE-gated: the EF returns them as null for client callers,
 *     and this page additionally never renders them when
 *     `viewerIsClient` — a fabricated «0» is never shown.
 *   - Row fields are the EF's client-safe projection by construction
 *     (title/platform/format/status/dates only).
 */

import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getClientMonthlyReport } from '../../api/endpoints';
import type {
  ClientMonthlyReportData,
  MonthlyReportBucket,
  MonthlyReportCountRow,
  MonthlyReportRow,
} from '../../api/types';
import { isApiError } from '../../api/client';
import { useMeContext } from '../../context/MeContextProvider';
import { useClientScope } from '../../context/ClientScopeProvider';
import { hasRoleCategory } from '../../permissions';
import { EmptyState, ErrorState, LoadingState, NotFoundState } from '../../components/states';
import { DEFAULT_TIMEZONE, TIMEZONE_OPTIONS } from './operatingSetup';
import { OrganicPerformanceSection } from './OrganicPerformanceSection';

type ReportState =
  | { readonly status: 'idle' }
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly report: ClientMonthlyReportData }
  | { readonly status: 'not-found' }
  | { readonly status: 'error'; readonly error: unknown };

/**
 * Current month as YYYY-MM in the DEFAULT report timezone (Codex 289A
 * P2: a UTC-derived default disagrees with the Asia/Riyadh window for
 * the first hours of a new month). en-CA yields zero-padded
 * YYYY-MM-DD parts.
 */
function currentMonthYYYYMM(now = new Date()): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: DEFAULT_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
  }).formatToParts(now);
  const get = (type: string): string => parts.find((p) => p.type === type)?.value ?? '';
  const yyyy = get('year');
  const mm = get('month');
  return yyyy && mm
    ? `${yyyy}-${mm}`
    : `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, '0')}`;
}

const BUCKET_LABEL_AR: Readonly<Record<MonthlyReportBucket, string>> = {
  planned: 'مخطط',
  in_production: 'قيد الإنتاج',
  awaiting_client: 'بانتظار العميل',
  approved_ready: 'معتمد وجاهز',
  published: 'منشور',
  cancelled: 'ملغي',
};

/** KPI order. Agency-only buckets render only for agency viewers. */
const KPI_BUCKETS: ReadonlyArray<{
  readonly bucket: MonthlyReportBucket;
  readonly agencyOnly: boolean;
}> = [
  { bucket: 'planned', agencyOnly: true },
  { bucket: 'in_production', agencyOnly: true },
  { bucket: 'awaiting_client', agencyOnly: false },
  { bucket: 'approved_ready', agencyOnly: false },
  { bucket: 'published', agencyOnly: false },
];

// Mirrors the canonical `content_item_status` Arabic copy
// (ContentPage `contentStatusLabel`) — kept local per repo convention
// (label maps are per-page locals, e.g. PublishingPage duplicates the
// platform map the same way).
const ITEM_STATUS_LABEL_AR: Readonly<Record<string, string>> = {
  draft: 'مسودة',
  pending_client_content_approval: 'بانتظار موافقة العميل',
  client_content_approved: 'وافق العميل على المحتوى',
  in_production: 'قيد الإنتاج',
  pending_internal_review: 'مراجعة داخلية',
  internal_approved: 'معتمد داخلياً',
  pending_client_asset_approval: 'بانتظار الموافقة النهائية',
  client_asset_approved: 'جاهز للنشر',
  scheduled: 'مجدول',
  published: 'منشور',
  cancelled: 'ملغي',
};

const ITEM_STATUS_PILL: Readonly<Record<string, string>> = {
  draft: 'pill-draft',
  pending_client_content_approval: 'pill-review',
  client_content_approved: 'pill-info',
  in_production: 'pill-review',
  pending_internal_review: 'pill-review',
  internal_approved: 'pill-info',
  pending_client_asset_approval: 'pill-review',
  client_asset_approved: 'pill-info',
  scheduled: 'pill-info',
  published: 'pill-active',
  cancelled: 'pill-archived',
};

const PLATFORM_LABEL_AR: Readonly<Record<string, string>> = {
  instagram_feed: 'منشور انستقرام',
  instagram_story: 'ستوري انستقرام',
  instagram_reel: 'ريلز انستقرام',
  facebook_post: 'منشور فيسبوك',
  facebook_story: 'ستوري فيسبوك',
  tiktok_video: 'فيديو تيك توك',
  snapchat_story: 'ستوري سناب شات',
};

const DISCIPLINE_LABEL_AR: Readonly<Record<string, string>> = {
  content: 'محتوى كتابي',
  design: 'تصميم',
  video: 'فيديو',
};

const ASSET_FORMAT_LABEL_AR: Readonly<Record<string, string>> = {
  static_image: 'صورة ثابتة',
  carousel: 'كاروسيل',
  vertical_video: 'فيديو عمودي',
  story: 'ستوري',
  unspecified: 'غير محدد',
};

function platformLabel(key: string): string {
  return PLATFORM_LABEL_AR[key] ?? key;
}

function disciplineLabel(key: string): string {
  return DISCIPLINE_LABEL_AR[key] ?? key;
}

function assetFormatLabel(key: string): string {
  return ASSET_FORMAT_LABEL_AR[key] ?? key;
}

function itemStatusLabel(key: string): string {
  return ITEM_STATUS_LABEL_AR[key] ?? key;
}

function formatReportDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('ar-SA');
}

export function ReportsPage(): JSX.Element | null {
  const { clientId } = useParams();
  const { me } = useMeContext();
  const { clients } = useClientScope();

  // Task 286G idiom — re-derived locally (ContentPage.tsx pattern):
  // a viewer with a client-category role and NO agency role is a
  // client-portal viewer; dual-hat agency+client actors count as
  // agency.
  const hasClientCategoryRole = useMemo(() => {
    if (!me) return false;
    for (const r of me.roles) {
      if (r.category === 'client') return true;
    }
    return false;
  }, [me]);
  const viewerIsAgencyActor = useMemo(() => !!me && hasRoleCategory(me, 'agency'), [me]);
  const viewerIsClient = hasClientCategoryRole && !viewerIsAgencyActor;

  const clientName = useMemo(
    () => clients.find((c) => c.id === clientId)?.name ?? null,
    [clients, clientId],
  );

  const [month, setMonth] = useState<string>(() => currentMonthYYYYMM());
  const [timezone, setTimezone] = useState<string>(DEFAULT_TIMEZONE);
  const [reloadKey, setReloadKey] = useState(0);
  const [state, setState] = useState<ReportState>({ status: 'idle' });

  useEffect(() => {
    if (!clientId) return;
    const ctrl = new AbortController();
    setState({ status: 'loading' });
    getClientMonthlyReport({ clientId, month, timezone, signal: ctrl.signal })
      .then((report) => {
        if (ctrl.signal.aborted) return;
        setState({ status: 'ready', report });
      })
      .catch((err: unknown) => {
        if (ctrl.signal.aborted) return;
        if (err instanceof DOMException && err.name === 'AbortError') return;
        if (isApiError(err) && err.code === 'not_found') {
          setState({ status: 'not-found' });
          return;
        }
        setState({ status: 'error', error: err });
      });
    return () => ctrl.abort();
  }, [clientId, month, timezone, reloadKey]);

  if (!clientId) return null;

  return (
    <div className="page stack-md">
      <div className="page-head">
        <div>
          <h1 className="h-page">التقرير الشهري</h1>
          <p className="sub">
            {clientName ? `${clientName} — ` : ''}
            ملخص تشغيلي لخطة المحتوى والنشر خلال الشهر المحدد.
          </p>
        </div>
      </div>

      <div className="card">
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: 12,
          }}
        >
          <label>
            <span className="lbl">الشهر</span>
            <input
              type="month"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              aria-label="شهر التقرير"
            />
          </label>
          <label>
            <span className="lbl">المنطقة الزمنية</span>
            <select value={timezone} onChange={(e) => setTimezone(e.target.value)}>
              {TIMEZONE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </label>
          <div style={{ alignSelf: 'end' }}>
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={() => setReloadKey((n) => n + 1)}
            >
              تحديث
            </button>
          </div>
        </div>
      </div>

      {state.status === 'loading' || state.status === 'idle' ? (
        <LoadingState title="جاري تحميل التقرير الشهري…" />
      ) : null}

      {state.status === 'not-found' ? (
        <NotFoundState
          title="التقرير غير متاح"
          description="هذا العميل غير متاح لحسابك أو غير موجود."
        />
      ) : null}

      {state.status === 'error' ? (
        <ErrorState
          title="تعذر تحميل التقرير"
          error={state.error}
          onRetry={() => setReloadKey((n) => n + 1)}
        />
      ) : null}

      {state.status === 'ready' ? (
        <ReportBody report={state.report} viewerIsClient={viewerIsClient} />
      ) : null}

      {/* Task 309A — organic content performance. A sibling section with its
          own period/platform filters and its own fetch: the monthly pipeline
          report above answers "did the work ship?", this one answers "how did
          it perform?", and neither can be derived from the other. */}
      <OrganicPerformanceSection clientId={clientId} />
    </div>
  );
}

interface ReportBodyProps {
  readonly report: ClientMonthlyReportData;
  readonly viewerIsClient: boolean;
}

function ReportBody(props: ReportBodyProps): JSX.Element {
  const { report, viewerIsClient } = props;
  // 286J double-gate: the EF already nulls agency-only buckets for
  // client callers; the FE additionally refuses to render them for a
  // client viewer even if a value were present.
  const showAgencyBuckets = report.viewer_is_agency && !viewerIsClient;
  const truncated = report.totals.calendar_truncated || report.totals.publish_truncated;

  if (report.rows.length === 0) {
    return (
      <EmptyState
        title="لا توجد بيانات لهذا الشهر"
        description="لم يتم العثور على مواد مجدولة أو منشورة خلال الشهر المحدد لهذا العميل."
      />
    );
  }

  return (
    <div className="stack-md">
      {truncated ? (
        <div className="banner banner-info" role="status">
          القائمة طويلة وتم اقتصاص جزء منها؛ الأرقام أدناه تعكس الجزء المحمّل فقط.
        </div>
      ) : null}

      <section aria-label="مؤشرات الشهر">
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
            gap: 12,
          }}
        >
          {KPI_BUCKETS.map(({ bucket, agencyOnly }) => {
            if (agencyOnly && !showAgencyBuckets) return null;
            const value = report.buckets[bucket];
            if (value === null) return null;
            return (
              <div key={bucket} className="card kpi">
                <span className="lbl">{BUCKET_LABEL_AR[bucket]}</span>
                <span className="v">{value}</span>
              </div>
            );
          })}
        </div>
        {showAgencyBuckets && (report.buckets.cancelled ?? 0) > 0 ? (
          <p className="tiny" style={{ marginTop: 8 }}>
            مواد ملغاة خلال الشهر: {report.buckets.cancelled} (غير محسوبة ضمن المؤشرات أعلاه).
          </p>
        ) : null}
      </section>

      <section aria-label="التفصيل" className="stack-md">
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
            gap: 12,
          }}
        >
          <BreakdownCard
            title="حسب المنصة"
            unit="مواضع"
            rows={report.by_platform}
            labelOf={platformLabel}
          />
          <BreakdownCard
            title="حسب نوع المحتوى"
            unit="مواد"
            rows={report.by_discipline}
            labelOf={disciplineLabel}
          />
          <BreakdownCard
            title="حسب صيغة التصميم"
            unit="مواضع"
            rows={report.by_asset_format}
            labelOf={assetFormatLabel}
          />
        </div>
      </section>

      <section aria-label="مواد الشهر" className="card">
        <div className="sec-title">
          <h2>مواد الشهر</h2>
          <span className="meta">
            {report.totals.items} مادة · {report.totals.placements} موضع نشر ·{' '}
            {report.totals.published_placements} منشور فعلياً
          </span>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table className="tbl">
            <thead>
              <tr>
                <th className="name">العنوان</th>
                <th>المنصة</th>
                <th>الصيغة</th>
                <th>الحالة</th>
                <th>موعد الجدولة</th>
                <th>تاريخ النشر</th>
              </tr>
            </thead>
            <tbody>
              {sortRows(report.rows).map((row, idx) => (
                <tr key={`${row.content_item_id}-${row.platform}-${idx}`}>
                  <td className="name">{row.title}</td>
                  <td>{platformLabel(row.platform)}</td>
                  <td>{row.asset_format ? assetFormatLabel(row.asset_format) : '—'}</td>
                  <td>
                    <span className={`pill ${ITEM_STATUS_PILL[row.item_status] ?? 'pill-draft'}`}>
                      {itemStatusLabel(row.item_status)}
                    </span>
                  </td>
                  <td>
                    <span style={{ direction: 'ltr', textAlign: 'start', display: 'inline-block' }}>
                      {formatReportDate(row.scheduled_for)}
                    </span>
                  </td>
                  <td>
                    <span style={{ direction: 'ltr', textAlign: 'start', display: 'inline-block' }}>
                      {formatReportDate(row.published_at)}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

interface BreakdownCardProps {
  readonly title: string;
  readonly unit: string;
  readonly rows: ReadonlyArray<MonthlyReportCountRow>;
  readonly labelOf: (key: string) => string;
}

function BreakdownCard(props: BreakdownCardProps): JSX.Element {
  return (
    <div className="card">
      <div className="sec-title">
        <h2>{props.title}</h2>
        <span className="meta">{props.unit}</span>
      </div>
      {props.rows.length === 0 ? (
        <p className="tiny">لا بيانات.</p>
      ) : (
        <table className="tbl">
          <tbody>
            {props.rows.map((row) => (
              <tr key={row.key}>
                <td className="name">{props.labelOf(row.key)}</td>
                <td className="num">{row.count}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

/** Scheduled rows in slot order first, then published-without-a-slot
 *  rows by publish time. */
function sortRows(rows: ReadonlyArray<MonthlyReportRow>): ReadonlyArray<MonthlyReportRow> {
  return [...rows].sort((a, b) => {
    const ka = a.scheduled_for ?? a.published_at ?? '';
    const kb = b.scheduled_for ?? b.published_at ?? '';
    return ka.localeCompare(kb);
  });
}
